import { useEffect } from "react";
import { Outlet, NavLink, Link, useLocation, useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { BadgeCheck, Bell, FileText, Flame, Home, LifeBuoy, Lock, LogOut, UserCog } from "lucide-react";
import { api } from "@/lib/api";
import Wordmark from "@/components/lux/Wordmark";
import { StatusPill } from "@/components/lux/Badges";
import { AdvertiserProvider, useAdvertiser } from "./useAdvertiser";

function PageSplash({ label = "Loading your dashboard" }) {
  return (
    <div className="flex min-h-[70vh] items-center justify-center text-sm text-lux-muted" data-testid="advertiser-loading">
      <span>{label}...</span>
    </div>
  );
}

const NAV = [
  { to: "/advertiser", label: "Overview", icon: Home, testId: "nav-overview", end: true },
  { to: "/advertiser/verify", label: "Verification", icon: BadgeCheck, testId: "nav-verification" },
  { to: "/advertiser/listing", label: "Listing", icon: FileText, testId: "nav-listing" },
  { to: "/advertiser/stories", label: "Stories", icon: Flame, testId: "nav-stories" },
  { to: "/advertiser/notifications", label: "Notifications", icon: Bell, testId: "nav-notifications" },
  { to: "/advertiser/support", label: "Support", icon: LifeBuoy, testId: "nav-support" },
  { to: "/advertiser/account", label: "Account & data", icon: UserCog, testId: "nav-account" },
  { to: "/advertiser/security", label: "Security", icon: Lock, testId: "nav-security" },
];

function Shell() {
  const { session, sessionLoading, isAuthenticated, isPending, needs2faSetup, me } = useAdvertiser();
  const location = useLocation();
  const navigate = useNavigate();
  const client = useQueryClient();

  useEffect(() => {
    document.documentElement.classList.remove("admin-theme");
    document.documentElement.classList.add("dark");
  }, []);

  useEffect(() => {
    if (sessionLoading) return;
    const path = location.pathname;
    const isAuthPage = path.startsWith("/advertiser/login") || path.startsWith("/advertiser/register") || path.startsWith("/advertiser/2fa");
    if (!session) return;
    if (!isAuthenticated && !isPending && !isAuthPage) {
      navigate("/advertiser/login", { replace: true });
    } else if (!isAuthenticated && isPending && !isAuthPage) {
      navigate(needs2faSetup ? "/advertiser/2fa?mode=setup" : "/advertiser/2fa?mode=login", { replace: true });
    } else if (isAuthenticated && (path === "/advertiser/login" || path === "/advertiser/register" || path.startsWith("/advertiser/2fa"))) {
      navigate("/advertiser", { replace: true });
    }
  }, [sessionLoading, session, isAuthenticated, isPending, needs2faSetup, location.pathname, navigate]);

  const path = location.pathname;
  const isAuthPage = path === "/advertiser/login" || path === "/advertiser/register" || path.startsWith("/advertiser/2fa");

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch (_) {}
    client.clear();
    toast.success("Signed out");
    navigate("/advertiser/login", { replace: true });
  };

  if (isAuthPage) {
    return (
      <div className="min-h-screen bg-lux-bg text-lux-text">
        <div className="mx-auto flex min-h-screen max-w-md flex-col justify-center px-5 py-10">
          <Link to="/" className="self-center" data-testid="advertiser-auth-wordmark">
            <Wordmark size="text-3xl" to={null} />
          </Link>
          <div className="mt-8">
            <Outlet />
          </div>
          <p className="mt-10 text-center text-xs text-lux-muted">
            <Link to="/" className="lux-link" data-testid="advertiser-auth-back-link">Back to the public site</Link>
          </p>
        </div>
      </div>
    );
  }

  if (sessionLoading || !isAuthenticated) {
    return <div className="min-h-screen bg-lux-bg text-lux-text"><PageSplash /></div>;
  }

  return (
    <div className="min-h-screen bg-lux-bg text-lux-text">
      <header className="sticky top-0 z-30 border-b border-lux-border bg-[#0B0B0C]/90 backdrop-blur">
        <div className="mx-auto flex h-16 max-w-7xl items-center justify-between gap-4 px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-6">
            <Wordmark size="text-2xl" to="/" testId="advertiser-header-wordmark" glow />
            <span className="hidden text-xs text-lux-muted sm:inline">Advertiser dashboard</span>
          </div>
          <div className="flex items-center gap-3">
            {me?.status && <StatusPill status={me.status} testId="advertiser-status-pill" />}
            <button
              onClick={logout}
              className="lux-btn lux-btn-ghost lux-btn-sm"
              data-testid="advertiser-logout-button"
              aria-label="Sign out"
            >
              <LogOut className="h-4 w-4" /> Sign out
            </button>
          </div>
        </div>
      </header>
      <div className="mx-auto flex max-w-7xl flex-col gap-6 px-4 py-6 sm:px-6 lg:flex-row lg:gap-8 lg:px-8 lg:py-10">
        <aside className="lg:w-56 lg:shrink-0">
          <nav aria-label="Advertiser" className="lux-scroll-x -mx-4 flex gap-1 overflow-x-auto px-4 lg:mx-0 lg:flex-col lg:gap-1 lg:px-0" data-testid="advertiser-nav">
            {NAV.map(({ to, label, icon: Icon, testId, end }) => (
              <NavLink
                key={to}
                to={to}
                end={end}
                className={({ isActive }) =>
                  `flex shrink-0 items-center gap-2.5 whitespace-nowrap rounded-[10px] px-3.5 py-2.5 text-sm transition-colors ${
                    isActive ? "bg-lux-surface text-lux-silverBright" : "text-lux-text/80 hover:bg-white/5 hover:text-lux-silverBright"
                  }`
                }
                data-testid={`advertiser-${testId}`}
              >
                <Icon className="h-4 w-4" aria-hidden="true" />
                {label}
                {label === "Notifications" && me?.unread_notifications ? (
                  <span className="ml-1 inline-flex min-w-[18px] justify-center rounded-full bg-lux-silverBright px-1.5 text-[10px] font-semibold text-black">
                    {me.unread_notifications}
                  </span>
                ) : null}
              </NavLink>
            ))}
          </nav>
        </aside>
        <main className="flex-1 min-w-0">
          <Outlet />
        </main>
      </div>
    </div>
  );
}

export default function AdvertiserLayout() {
  return (
    <AdvertiserProvider>
      <Shell />
    </AdvertiserProvider>
  );
}
